import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { PlatformClientService } from '@pure/pure1-ui-platform-angular';
import { FeatureFlagDxpService } from '@pure1/data';
import { map, Observable, of, switchMap, take } from 'rxjs';

import { EffectiveOrgService } from '../../data/services/effective-org.service';
import { ORG_URL_FF } from './org-url-shared';

/**
 * canActivate on the bare `''` parent. When the org-URL FF is OFF, primes
 * EffectiveOrgService from the platform's effective org (legacy access-token
 * claim path) so OrgHeadersInterceptor can attach org headers. When the FF is
 * ON, this guard no-ops — the URL is the source of truth and orgUidValidatorGuard
 * on the `/orgs/:orgUid` parent handles priming.
 *
 * Skips the platform call when EffectiveOrgService is already primed at boot
 * (by initializeApp/primeEffectiveOrg) to avoid a redundant async round-trip
 * on every navigation.
 */
export const platformOrgPrimerGuard: CanActivateFn = (): Observable<boolean> => {
    const featureFlagService = inject(FeatureFlagDxpService);
    const platformClientService = inject(PlatformClientService);
    const effectiveOrgService = inject(EffectiveOrgService);

    return featureFlagService.getFeatureFlag(ORG_URL_FF).pipe(
        take(1),
        switchMap(status => {
            if (status?.enabled) {
                return of(true);
            }
            return effectiveOrgService.effectiveOrg$.pipe(
                take(1),
                switchMap(current => {
                    if (current) return of(true);
                    return platformClientService.getEffectiveOrg().pipe(
                        take(1),
                        map(org => {
                            effectiveOrgService.setCurrentOrg(org ? { ...org, opCompanyName: null } : null);
                            return true;
                        }),
                    );
                }),
            );
        }),
    );
};
